import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// i18n del backoffice: español por defecto, inglés disponible. El idioma se
// persiste en localStorage (clave propia, no compartida con la app).
const STORAGE_KEY = 'bo_lang';

const resources = {
  es: {
    translation: {
      common: {
        loading: 'Cargando…',
        error: 'Algo ha ido mal. Inténtalo de nuevo.',
        search: 'Buscar…',
        all: 'Todos',
        yes: 'Sí',
        no: 'No',
        prev: 'Anterior',
        next: 'Siguiente',
        pageOf: '{{from}}–{{to}} de {{total}}',
        empty: 'Sin resultados',
        openInApp: 'Ver en la app',
        refresh: 'Refrescar',
      },
      login: {
        title: 'Backoffice',
        subtitle: 'Panel de administración de Bestigium',
        emailLabel: 'Email',
        emailPlaceholder: 'tu@email.com',
        passwordLabel: 'Contraseña',
        passwordPlaceholder: '••••••••',
        enter: 'Entrar',
        entering: 'Entrando…',
        enterEmail: 'Introduce tu email',
        enterPassword: 'Introduce tu contraseña',
        invalidCredentials: 'Email o contraseña incorrectos',
        emailNotVerified: 'Debes confirmar tu email antes de entrar.',
      },
      nav: {
        dashboard: 'Dashboard',
        users: 'Usuarios',
        points: 'Puntos',
        research: 'Investigación',
        feedback: 'Feedback',
        logout: 'Salir',
      },
      feedback: {
        title: 'Feedback',
        statuses: {
          new: 'Nuevo',
          reviewed: 'Revisado',
          resolved: 'Resuelto',
        },
        types: {
          bug: 'Error',
          idea: 'Idea',
          other: 'Otro',
        },
        actions: {
          new: 'Reabrir',
          reviewed: 'Marcar revisado',
          resolved: 'Marcar resuelto',
        },
      },
      dashboard: {
        title: 'Dashboard',
        users: 'Usuarios',
        usersVerified: 'verificados',
        usersPro: 'Pro',
        usersNew7d: 'nuevos (7 días)',
        usersNew30d: 'nuevos (30 días)',
        points: 'Puntos',
        pointsPublic: 'públicos',
        pointsInvestigated: 'investigados',
        pointsNotInvestigated: 'no investigados',
        pointsError: 'con error',
        pointsProcessed: 'procesados (investigados + error)',
        pointsNew7d: 'nuevos (7 días)',
        research: 'Investigación',
        researchQueued: 'en cola',
        researchRunning: 'en curso',
        researchDone: 'completadas',
        researchError: 'con error',
        content: 'Contenido',
        images: 'fotos',
        comments: 'comentarios',
        resources: 'enlaces',
        feedback: 'Feedback',
        feedbackNew: 'nuevo',
        feedbackReviewed: 'revisado',
        feedbackResolved: 'resuelto',
        byCategory: 'Puntos por categoría',
        byModel: 'Puntos por modelo de IA',
        modelUnknown: '(modelo desconocido)',
        byCommunity: 'Puntos por comunidad',
        community: 'Comunidad',
        communityTotal: 'Total',
        communityInvestigated: 'Investigados',
        communityError: 'Con error',
        communityPending: 'Pendientes',
        communityProgress: 'Progreso',
        recentUsers: 'Últimos usuarios',
        recentPoints: 'Últimos puntos',
        noTitle: '(sin título)',
      },
      users: {
        title: 'Usuarios',
        searchPlaceholder: 'Buscar por email o nombre…',
        email: 'Email',
        name: 'Nombre',
        role: 'Rol',
        pro: 'Pro',
        verified: 'Verificado',
        google: 'Vinculado con Google',
        points: 'Puntos',
        comments: 'Comentarios',
        registered: 'Alta',
      },
      points: {
        title: 'Puntos',
        searchPlaceholder: 'Buscar por título, descripción o autor…',
        allCategories: 'Todas las categorías',
        allVisibilities: 'Públicos y privados',
        public: 'Público',
        private: 'Privado',
        titleCol: 'Título',
        category: 'Categoría',
        author: 'Autor',
        model: 'Modelo',
        allModels: 'Todos los modelos',
        noModel: 'Sin modelo',
        photos: 'Fotos',
        visibility: 'Visibilidad',
        investigated: 'Investigado',
        created: 'Creado',
        relevance: 'Relevancia',
        allRelevances: 'Toda relevancia',
        relevanceLevel_1: '1 · Menor',
        relevanceLevel_2: '2 · Interés local',
        relevanceLevel_3: '3 · Relevante',
        relevanceLevel_4: '4 · Muy relevante',
        relevanceLevel_5: '5 · De referencia',
        relevanceSaveError: 'No se pudo guardar la relevancia',
        noTitle: '(sin título)',
        descriptionShort: 'descr. {{n}} car.',
      },
      research: {
        title: 'Peticiones de investigación',
        subtitle: 'Cola de "Rellenar con IA" (función Pro)',
        allStatuses: 'Todos los estados',
        point: 'Punto',
        requestedBy: 'Solicitada por',
        status: 'Estado',
        requested: 'Solicitada',
        updated: 'Actualizada',
        error: 'Error',
        statuses: {
          queued: 'En cola',
          running: 'En curso',
          done: 'Completada',
          error: 'Error',
        },
      },
      categories: {
        calzada: 'Calzada',
        puente: 'Puente',
        miliario: 'Miliario',
        villa: 'Villa',
        castro: 'Castro',
        dolmen: 'Dolmen',
        acueducto: 'Acueducto',
        necropolis: 'Necrópolis',
        muralla: 'Muralla',
        torre: 'Torre',
        balneario: 'Balneario',
        ciudad: 'Ciudad',
        teatro: 'Teatro',
        anfiteatro: 'Anfiteatro',
        circo: 'Circo',
        templo: 'Templo',
        foro: 'Foro',
        menhir: 'Menhir',
        cueva: 'Cueva',
        arte_rupestre: 'Arte rupestre',
        sepulcro: 'Sepulcro',
        castillo: 'Castillo',
        ermita: 'Ermita',
        mina: 'Mina',
        faro: 'Faro',
        carretera: 'Carretera',
        pozo: 'Pozo',
        fabrica_salado: 'Fábrica de salazón',
        desconocido: 'Desconocido',
        otro: 'Otro',
      },
    },
  },
  en: {
    translation: {
      common: {
        loading: 'Loading…',
        error: 'Something went wrong. Try again.',
        search: 'Search…',
        all: 'All',
        yes: 'Yes',
        no: 'No',
        prev: 'Previous',
        next: 'Next',
        pageOf: '{{from}}–{{to}} of {{total}}',
        empty: 'No results',
        openInApp: 'Open in app',
        refresh: 'Refresh',
      },
      login: {
        title: 'Backoffice',
        subtitle: 'Bestigium admin panel',
        emailLabel: 'Email',
        emailPlaceholder: 'you@email.com',
        passwordLabel: 'Password',
        passwordPlaceholder: '••••••••',
        enter: 'Sign in',
        entering: 'Signing in…',
        enterEmail: 'Enter your email',
        enterPassword: 'Enter your password',
        invalidCredentials: 'Wrong email or password',
        emailNotVerified: 'You must verify your email first.',
      },
      nav: {
        dashboard: 'Dashboard',
        users: 'Users',
        points: 'Points',
        research: 'Research',
        feedback: 'Feedback',
        logout: 'Log out',
      },
      feedback: {
        title: 'Feedback',
        statuses: {
          new: 'New',
          reviewed: 'Reviewed',
          resolved: 'Resolved',
        },
        types: {
          bug: 'Bug',
          idea: 'Idea',
          other: 'Other',
        },
        actions: {
          new: 'Reopen',
          reviewed: 'Mark reviewed',
          resolved: 'Mark resolved',
        },
      },
      dashboard: {
        title: 'Dashboard',
        users: 'Users',
        usersVerified: 'verified',
        usersPro: 'Pro',
        usersNew7d: 'new (7 days)',
        usersNew30d: 'new (30 days)',
        points: 'Points',
        pointsPublic: 'public',
        pointsInvestigated: 'investigated',
        pointsNotInvestigated: 'not investigated',
        pointsError: 'with error',
        pointsProcessed: 'processed (investigated + error)',
        pointsNew7d: 'new (7 days)',
        research: 'Research',
        researchQueued: 'queued',
        researchRunning: 'running',
        researchDone: 'done',
        researchError: 'failed',
        content: 'Content',
        images: 'photos',
        comments: 'comments',
        resources: 'links',
        feedback: 'Feedback',
        feedbackNew: 'new',
        feedbackReviewed: 'reviewed',
        feedbackResolved: 'resolved',
        byCategory: 'Points by category',
        byModel: 'Points by AI model',
        modelUnknown: '(unknown model)',
        byCommunity: 'Points by community',
        community: 'Community',
        communityTotal: 'Total',
        communityInvestigated: 'Investigated',
        communityError: 'With error',
        communityPending: 'Pending',
        communityProgress: 'Progress',
        recentUsers: 'Latest users',
        recentPoints: 'Latest points',
        noTitle: '(untitled)',
      },
      users: {
        title: 'Users',
        searchPlaceholder: 'Search by email or name…',
        email: 'Email',
        name: 'Name',
        role: 'Role',
        pro: 'Pro',
        verified: 'Verified',
        google: 'Linked with Google',
        points: 'Points',
        comments: 'Comments',
        registered: 'Joined',
      },
      points: {
        title: 'Points',
        searchPlaceholder: 'Search by title, description or author…',
        allCategories: 'All categories',
        allVisibilities: 'Public and private',
        public: 'Public',
        private: 'Private',
        titleCol: 'Title',
        category: 'Category',
        author: 'Author',
        model: 'Model',
        allModels: 'All models',
        noModel: 'No model',
        photos: 'Photos',
        visibility: 'Visibility',
        investigated: 'Investigated',
        created: 'Created',
        relevance: 'Relevance',
        allRelevances: 'Any relevance',
        relevanceLevel_1: '1 · Minor',
        relevanceLevel_2: '2 · Local interest',
        relevanceLevel_3: '3 · Relevant',
        relevanceLevel_4: '4 · Highly relevant',
        relevanceLevel_5: '5 · Landmark site',
        relevanceSaveError: 'Could not save the relevance',
        noTitle: '(untitled)',
        descriptionShort: 'descr. {{n}} chars',
      },
      research: {
        title: 'Research requests',
        subtitle: '"Fill with AI" queue (Pro feature)',
        allStatuses: 'All statuses',
        point: 'Point',
        requestedBy: 'Requested by',
        status: 'Status',
        requested: 'Requested',
        updated: 'Updated',
        error: 'Error',
        statuses: {
          queued: 'Queued',
          running: 'Running',
          done: 'Done',
          error: 'Error',
        },
      },
      categories: {
        calzada: 'Roman road',
        puente: 'Bridge',
        miliario: 'Milestone',
        villa: 'Villa',
        castro: 'Hillfort',
        dolmen: 'Dolmen',
        acueducto: 'Aqueduct',
        necropolis: 'Necropolis',
        muralla: 'Wall',
        torre: 'Tower',
        balneario: 'Baths',
        ciudad: 'City',
        teatro: 'Theatre',
        anfiteatro: 'Amphitheatre',
        circo: 'Circus',
        templo: 'Temple',
        foro: 'Forum',
        menhir: 'Menhir',
        cueva: 'Cave',
        arte_rupestre: 'Rock art',
        sepulcro: 'Tomb',
        castillo: 'Castle',
        ermita: 'Hermitage',
        mina: 'Mine',
        faro: 'Lighthouse',
        carretera: 'Road',
        desconocido: 'Unknown',
        otro: 'Other',
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: localStorage.getItem(STORAGE_KEY) ?? 'es',
  fallbackLng: 'es',
  interpolation: { escapeValue: false },
});

i18n.on('languageChanged', (lng) => localStorage.setItem(STORAGE_KEY, lng));

export default i18n;
